import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import notify from 'devextreme/ui/notify';

/**
 * Connects to the SignalR NotificationHub and shows a DevExtreme toast
 * whenever a customer creates a new work task via the portal.
 * Scoped to the current tenant so only users of the same tenant see it.
 */
export function usePortalNotifications(token?: string, tenantName?: string) {
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  useEffect(() => {
    if (!token || !tenantName) return;

    const hubUrl = `${process.env.REACT_APP_URL}/hubs/notifications`;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    connection.onreconnecting((err) => console.warn('[SignalR] Reconnecting…', err));
    connection.onreconnected((id) => {
      console.log('[SignalR] Reconnected, connectionId:', id);
      connection.invoke('JoinTenantGroup', tenantName).catch(console.error);
    });
    connection.onclose((err) => console.warn('[SignalR] Connection closed', err));

    connection.on('NewPortalTask', (task: {
      oid: string;
      taskDescription: string;
      referenceNumber?: string;
      relationName: string;
      locationName: string;
    }) => {
      console.log('[SignalR] Received NewPortalTask:', task);
      const location = task.locationName || task.relationName || '';
      const ref = task.referenceNumber ? `#${task.referenceNumber}` : '';

      notify(
        {
          message: ' ',
          type: 'info',
          displayTime: 10000,
          width: 420,
          contentTemplate: (element: { append: (el: HTMLElement) => void }) => {
            const container = document.createElement('div');
            container.style.cssText = 'display:flex;align-items:flex-start;gap:12px;padding:4px 0;';

            const icon = document.createElement('div');
            icon.style.cssText =
              'flex-shrink:0;width:38px;height:38px;border-radius:50%;' +
              'background:rgba(255,255,255,0.2);display:flex;align-items:center;' +
              'justify-content:center;font-size:18px;';
            icon.textContent = '📋';

            const body = document.createElement('div');
            body.style.cssText = 'flex:1;min-width:0;';

            const title = document.createElement('div');
            title.style.cssText = 'font-weight:600;font-size:14px;margin-bottom:2px;';
            title.textContent = 'Nieuwe melding via portaal';

            const desc = document.createElement('div');
            desc.style.cssText =
              'font-size:13px;opacity:0.95;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
            desc.textContent = task.taskDescription;

            body.appendChild(title);
            body.appendChild(desc);

            if (location) {
              const loc = document.createElement('div');
              loc.style.cssText = 'font-size:12px;opacity:0.8;margin-top:2px;';
              loc.textContent = `📍 ${location}`;
              body.appendChild(loc);
            }
            if (ref) {
              const refEl = document.createElement('div');
              refEl.style.cssText = 'font-size:12px;opacity:0.8;margin-top:1px;';
              refEl.textContent = `Ref: ${ref}`;
              body.appendChild(refEl);
            }

            container.appendChild(icon);
            container.appendChild(body);
            element.append(container);
          },
        },
        { position: 'top right', direction: 'down-push' },
      );
    });

    console.log('[SignalR] Starting connection… tenant:', tenantName);
    connection.start()
      .then(() => {
        console.log('[SignalR] Connected! connectionId:', connection.connectionId);
        return connection.invoke('JoinTenantGroup', tenantName);
      })
      .then(() => console.log('[SignalR] Joined tenant group:', tenantName))
      .catch((err) => {
        console.error('[SignalR] Connection failed:', err);
      });

    return () => {
      connection.stop();
    };
  }, [token, tenantName]);
}
